import React, { useEffect, useState } from "react";
import { useStores } from "../../../use-stores";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Collapse,
  Container,
  Grid2,
  IconButton,
  Paper,
  useTheme,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { observer } from "mobx-react";
import {
  VoucherEmailerFilters,
  parseQueryParamsForVoucherEmailerFilters,
} from "../../../../../models/Vouchers/VoucherEmailerFilters";
import { SortType } from "../../../../../models/SortType";
import VoucherEmailersTable from "../../components/emailers/VoucherEmailersTable";
import { useNotification } from "../../../../../utils/NotificationsContext";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import FormTextField from "../../../../../views/components/common/form/FormTextField";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import PermissionBlockLabel from "../../../../../views/components/common/pop-up/PermissionBlockLabel ";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import { EmailerStore } from "../../../stores/EmailerStore";

const VoucherEmailersListing: React.FC = () => {
  const theme = useTheme();
  const { userStore } = useStores();

  const { showError } = useNotification();

  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const [emailerStore] = useState(() => new EmailerStore());

  const initialFilters: VoucherEmailerFilters =
    parseQueryParamsForVoucherEmailerFilters(null, searchParams);

  const [filters, setFilters] = useState<VoucherEmailerFilters>(initialFilters);

  const [searchTerm, setSearchTerm] = useState(
    searchParams.get("search_text") || "",
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState(true);

  useEffect(() => {
    if (userStore.hasPermission([EmployeeRoles.VIEW_EMAILERS])) {
      setIsLoading(true);
      emailerStore
        .getVoucherEmailers(filters)
        .catch((err) =>
          showError(
            "Error",
            err?.response?.data?.message ||
              "Something went wrong! Please try again later.",
          ),
        )
        .finally(() => {
          setIsLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    const updateQueryParams = () => {
      const searchParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined) {
          const paramValue = Array.isArray(value)
            ? value.join(",")
            : value?.toString() || "";
          searchParams.set(key, paramValue);
        }
      });

      const queryString = searchParams.toString();
      if (queryString) {
        navigate({ search: queryString }, { replace: true });
      }
    };

    updateQueryParams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, navigate]);

  const handleSearchBar = async (searchText: string | undefined) => {
    if (searchText && searchText.length !== 0) {
      setSearchTerm(searchText);
    } else {
      setSearchTerm("");
      setFilters((prev) => ({
        ...prev,
        search_text: "",
      }));
    }
  };

  const handleSearchSubmit = () => {
    setFilters((prev) => ({
      ...prev,
      search_text: searchTerm,
      page_no: 1,
    }));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      if (searchTerm.trim().length > 0) {
        setFilters((prev) => ({
          ...prev,
          search_text: searchTerm,
          page_no: 1,
        }));
      } else {
        setSearchTerm("");
        setFilters((prev) => ({
          ...prev,
          search_text: "",
          page_no: 1,
        }));
      }
    }
  };

  const resetSearch = () => {
    setSearchTerm("");
    setFilters({
      page_no: 1,
      limit: 30,
      sort_by: "id",
      sort_type: SortType.DESC,
    });
  };

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setFilters((prev) => ({
      ...prev,
      page_no: newPage,
    }));
  };

  const handleRowsPerPageChange = (newLimit: number) => {
    setFilters((prev) => ({
      ...prev,
      limit: newLimit,
      page_no: 1,
    }));
  };

  return (
    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }} sx={{ px: 2 }}>
      {!userStore.hasPermission([EmployeeRoles.VIEW_EMAILERS]) && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Paper
            sx={{
              mt: 3,
              p: { xs: 2, md: 3 },
              borderRadius: 2,
              boxShadow:
                "rgba(0, 0, 0, 0.15) 0px 4px 24px, rgba(0, 0, 0, 0.05) 0px 8px 32px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                gap: 0.4,
              }}
            >
              <CustomTypography variant="heading3">
                Voucher Emailers
              </CustomTypography>
              <CustomTypography
                variant="body2"
                color={theme.palette.customColors?.greyTextColor}
              >
                View and manage voucher emailers. Search by subject or email.
              </CustomTypography>
            </Box>
          </Paper>
          <PermissionBlockLabel />
        </Box>
      )}
      {userStore.hasPermission([EmployeeRoles.VIEW_EMAILERS]) && (
        <Paper
          sx={{
            mt: 3,
            p: { xs: 2, md: 3 },
            borderRadius: 2,
            boxShadow:
              "rgba(0, 0, 0, 0.15) 0px 4px 24px, rgba(0, 0, 0, 0.05) 0px 8px 32px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              gap: 0.5,
            }}
          >
            <CustomTypography variant="heading3">
              Voucher Emailers
            </CustomTypography>
            <CustomTypography
              variant="body2"
              color={theme.palette.customColors?.greyTextColor}
            >
              View and manage voucher emailers. Search by subject or email.
            </CustomTypography>
          </Box>

          <Paper
            variant="outlined"
            sx={{
              mt: 2,
              borderRadius: 2,
              borderColor: "divider",
              overflow: "hidden",
            }}
          >
            <Box
              onClick={() => setIsSearchExpanded(!isSearchExpanded)}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 2,
                py: 0.5,
                cursor: "pointer",
                borderBottom: isSearchExpanded ? "1px solid" : "none",
                borderColor: "divider",
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <CustomTypography variant="heading6">Search</CustomTypography>
              <IconButton size="small">
                {isSearchExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>
            <Collapse in={isSearchExpanded} timeout="auto" unmountOnExit>
              <Box sx={{ p: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: 1.5,
                  }}
                >
                  <FormTextField
                    id="voucher-emailer-search"
                    placeholder="Search by subject or email"
                    type="search"
                    variant="outlined"
                    value={searchTerm}
                    onChange={(_, value) => handleSearchBar(value)}
                    onKeyDown={(e) => handleKeyDown(e)}
                  />
                  <AppButton
                    variant="primary"
                    label="Search"
                    onClick={handleSearchSubmit}
                    customStyles={{ minWidth: 140, height: 40 }}
                  />
                  <AppButton
                    variant="transparentPrimary"
                    label="Reset Search"
                    disabled={!searchTerm || searchTerm === ""}
                    onClick={resetSearch}
                    customStyles={{ minWidth: 140, height: 40 }}
                  />
                </Box>
              </Box>
            </Collapse>
          </Paper>

          {isLoading && (
            <Container
              sx={{
                minHeight: "40vh",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress />
            </Container>
          )}

          {!isLoading && (
            <Box mt={3}>
              {emailerStore.voucherEmailers.length === 0 && (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  height="10rem"
                  textAlign="center"
                  border="1px dashed"
                  borderColor="divider"
                  borderRadius={2}
                >
                  <CustomTypography
                    variant="heading6"
                    fontWeight={500}
                    color={theme.palette.customColors?.greyTextColor}
                  >
                    No voucher emailers match the current search criteria.
                  </CustomTypography>
                </Box>
              )}
              {emailerStore.voucherEmailers.length > 0 && (
                <VoucherEmailersTable
                  maxHeight={window.innerHeight - 350}
                  voucherEmailers={emailerStore.voucherEmailers}
                  limit={emailerStore.limit}
                  page_no={filters.page_no}
                  total_count={emailerStore.total_count}
                  total_pages={emailerStore.total_pages}
                  sortBy={filters.sort_by}
                  sortType={filters.sort_type}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleRowsPerPageChange}
                  onSortChange={(column, sortType) => {
                    setFilters((prev) => ({
                      ...prev,
                      sort_by: column,
                      sort_type: sortType,
                    }));
                  }}
                />
              )}
            </Box>
          )}
        </Paper>
      )}
    </Grid2>
  );
};

export default observer(VoucherEmailersListing);
